import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import CornerToast from '../shared/feedback/CornerToast';

const ToastContext = createContext(null);

/** Map API/axios messages to clear English; network errors get a friendly line. */
export function normalizeErrorMessage(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return 'Something went wrong. Please try again.';
  const lower = raw.toLowerCase();

  if (
    lower === 'network error' ||
    lower.includes('network error') ||
    lower.includes('err_network') ||
    lower.includes('failed to fetch') ||
    lower.includes('unable to connect') ||
    lower.includes('connection refused')
  ) {
    return 'Network error: unable to reach the server. Check your connection and try again.';
  }

  let msg = raw.replace(/^error:\s*/i, '').trim();
  return msg || 'Something went wrong. Please try again.';
}

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  /**
   * @param {string} message
   * @param {'error'|'warning'|'success'} [variant='error']
   * @param {number} [duration=4000]
   */
  const showToast = useCallback((message, variant = 'error', duration = 4000) => {
    const text =
      variant === 'error'
        ? normalizeErrorMessage(message)
        : String(message || '').trim() || 'Notice';
    setToast({
      key: Date.now(),
      message: text,
      variant,
      duration,
    });
  }, []);

  const value = useMemo(
    () => ({
      showToast,
      showError: (msg, duration) => showToast(msg, 'error', duration ?? 4000),
      showWarning: (msg, duration) => showToast(msg, 'warning', duration ?? 3500),
      showSuccess: (msg, duration) => showToast(msg, 'success', duration ?? 3000),
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        <CornerToast
          key={toast.key}
          toastKey={toast.key}
          message={toast.message}
          variant={toast.variant}
          duration={toast.duration}
          onExited={hideToast}
        />
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
